import { useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';

const LIMITE_INACTIVIDAD = 3 * 60 * 60 * 1000;
const INTERVALO_CHEQUEO = 60 * 1000;
const THROTTLE_ACTIVIDAD = 15 * 1000;
const ACTIVIDAD_KEY = 'portalLastActivity';

// fetch original, capturado antes de que el interceptor lo reemplace
const nativeFetch = typeof window !== 'undefined' ? window.fetch.bind(window) : null;

const decodeJWT = (token) => {
  try {
    const payload = token.split('.')[1];
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - payload.length % 4) % 4);
    return JSON.parse(atob(padded));
  } catch (error) {
    return null;
  }
};

const tokenExpirado = (token) => {
  const payload = decodeJWT(token);
  return Boolean(payload?.exp) && Date.now() >= payload.exp * 1000;
};

const leerUltimaActividad = () => {
  try {
    const valor = Number(localStorage.getItem(ACTIVIDAD_KEY));
    return valor > 0 ? valor : null;
  } catch (error) {
    return null;
  }
};

const guardarActividad = (timestamp) => {
  try {
    localStorage.setItem(ACTIVIDAD_KEY, String(timestamp));
  } catch (error) {
    // Sin localStorage no se puede persistir; el resto de chequeos sigue funcionando
  }
};

/**
 * Hook para gestión de sesión en producción
 * - Cierra la sesión si el JWT venció o hay más de 3 horas sin actividad del usuario
 * - La última actividad se guarda en localStorage (vale entre recargas y pestañas)
 * - Un 401/403 de la API solo cierra la sesión si verify-session confirma que ya no es válida
 */
const useSessionManager = (user, onLogout) => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const token = user?.token;
  const ultimaEscrituraRef = useRef(0);
  const cerrandoRef = useRef(false);

  const registrarActividad = useCallback(() => {
    const ahora = Date.now();
    if (ahora - ultimaEscrituraRef.current < THROTTLE_ACTIVIDAD) return;
    ultimaEscrituraRef.current = ahora;
    guardarActividad(ahora);
  }, []);

  const verificarSesion = useCallback(async () => {
    if (!token) return true;

    try {
      const response = await nativeFetch(`${API_BASE_URL}/auth/verify-session`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.status === 401 || response.status === 403) return false;
      // Un error del servidor no es motivo para cerrar la sesión
      if (!response.ok) return true;

      const data = await response.json();
      return Boolean(data.valid && data.success);

    } catch (error) {
      return true;
    }
  }, [token, API_BASE_URL]);

  // El aviso de "sesión expirada" lo lee Login; App.handleLogout lo conserva al limpiar el storage
  const cerrarSesion = useCallback((motivo) => {
    if (cerrandoRef.current) return;
    cerrandoRef.current = true;

    try {
      sessionStorage.setItem('sessionExpired', 'true');
      sessionStorage.setItem('sessionExpiredReason', motivo);
    } catch (error) {
      // Sin sessionStorage solo se pierde el aviso en el Login
    }

    if (onLogout) {
      onLogout();
    }
  }, [onLogout]);

  const cerrarSiSesionInvalida = useCallback(async () => {
    const esValida = await verificarSesion();
    if (!esValida) {
      cerrarSesion('expired');
    }
  }, [verificarSesion, cerrarSesion]);

  const chequearExpiracion = useCallback(() => {
    if (!token) return;

    if (tokenExpirado(token)) {
      cerrarSesion('expired');
      return;
    }

    const ultimaActividad = leerUltimaActividad();
    if (ultimaActividad && Date.now() - ultimaActividad > LIMITE_INACTIVIDAD) {
      cerrarSesion('inactivity');
    }
  }, [token, cerrarSesion]);

  // Interceptor de 401/403. Es layout effect para quedar instalado antes de los
  // useEffect de los hijos (PortalMain carga notificaciones al montar).
  useLayoutEffect(() => {
    if (!token || !API_BASE_URL) return;

    const fetchAnterior = window.fetch;

    window.fetch = async (...args) => {
      const response = await fetchAnterior(...args);

      if (response.status === 401 || response.status === 403) {
        const url = String(args[0]?.url ?? args[0]);
        if (url.startsWith(API_BASE_URL)) {
          cerrarSiSesionInvalida();
        }
      }

      return response;
    };

    return () => {
      window.fetch = fetchAnterior;
    };
  }, [token, API_BASE_URL, cerrarSiSesionInvalida]);

  // Chequeo al montar (sesión restaurada desde localStorage), periódico y al volver a la pestaña
  useEffect(() => {
    if (!token) return;

    if (!leerUltimaActividad()) {
      guardarActividad(Date.now());
    }

    chequearExpiracion();
    cerrarSiSesionInvalida();

    const intervalo = setInterval(chequearExpiracion, INTERVALO_CHEQUEO);

    const manejarVisibilidad = () => {
      if (document.visibilityState === 'visible') {
        chequearExpiracion();
      }
    };

    document.addEventListener('visibilitychange', manejarVisibilidad);
    window.addEventListener('focus', chequearExpiracion);

    return () => {
      clearInterval(intervalo);
      document.removeEventListener('visibilitychange', manejarVisibilidad);
      window.removeEventListener('focus', chequearExpiracion);
    };
  }, [token, chequearExpiracion, cerrarSiSesionInvalida]);

  // Detectar actividad del usuario
  useEffect(() => {
    if (!token) return;

    const eventos = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    eventos.forEach(evento => {
      window.addEventListener(evento, registrarActividad, { passive: true });
    });

    return () => {
      eventos.forEach(evento => {
        window.removeEventListener(evento, registrarActividad);
      });
    };
  }, [token, registrarActividad]);

  return { verificarSesion, registrarActividad };
};

/**
 * Alerta de sesión expirada
 */
const SessionExpiredAlert = ({ show, onClose }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-8 w-8 text-amber-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900">Sesión Expirada</h3>
          </div>
        </div>
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Su sesión ha expirado por inactividad. Por favor, inicie sesión nuevamente para continuar.
          </p>
        </div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Componente SessionManager
 */
const SessionManager = ({ user, onLogout, children }) => {
  useSessionManager(user, onLogout);
  return <>{children}</>;
};

export { SessionManager, useSessionManager, SessionExpiredAlert };
export default SessionManager;
